/**
 * Page Vision/RPA — E-6 US-135
 * Upload screenshot → analyse → visualisation éléments + guardrail
 */

import { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS, API_CONFIG } from '@/config/api';

interface Capture {
  id: string;
  agent_id: string;
  screen_name: string;
  guardrail_status: 'unknown' | 'ok' | 'ko';
  nb_elements: number;
  captured_at: string | null;
}

interface Element {
  label: string;
  type: string;
  bbox: [number, number, number, number];
  confidence: number;
}

interface GuardrailResult {
  ok: boolean;
  score: number;
  reason: string;
  found: string[];
  missing: string[];
}

const GUARDRAIL_BADGE: Record<string, string> = {
  unknown: 'bg-secondary',
  ok: 'bg-success',
  ko: 'bg-danger',
};

const TYPE_BADGE: Record<string, string> = {
  button: 'bg-primary',
  field: 'bg-info text-dark',
  label: 'bg-light text-dark border',
};

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function VisionPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [screenName, setScreenName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [loadingCaptures, setLoadingCaptures] = useState(false);
  const [selectedCapture, setSelectedCapture] = useState<string | null>(null);
  const [elements, setElements] = useState<Element[]>([]);
  const [guardInput, setGuardInput] = useState('');
  const [guardrailResult, setGuardrailResult] = useState<GuardrailResult | null>(null);
  const [runningGuard, setRunningGuard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadCaptures = async () => {
    setLoadingCaptures(true);
    try {
      const resp = await fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.rpaAdminCaptures}`, {
        headers: authHeader(),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      setCaptures(data.items ?? []);
    } catch (e: unknown) {
      setError('Erreur chargement captures');
    } finally {
      setLoadingCaptures(false);
    }
  };

  useEffect(() => { loadCaptures(); }, []);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !screenName.trim()) {
      setError('Sélectionnez un fichier et nommez l\'écran');
      return;
    }
    setUploading(true);
    setError(null);
    setElements([]);
    setSelectedCapture(null);
    setGuardrailResult(null);

    const form = new FormData();
    form.append('file', file);

    try {
      const resp = await fetch(
        `${API_CONFIG.baseURL}${API_ENDPOINTS.rpaAdminAnalyze(screenName.trim())}`,
        { method: 'POST', headers: authHeader(), body: form },
      );
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      setSelectedCapture(data.capture_id);
      setElements(data.elements ?? []);
      setSuccessMsg(`${data.nb_elements} éléments détectés`);
      setTimeout(() => setSuccessMsg(null), 3000);
      await loadCaptures();
    } catch (e: unknown) {
      setError('Erreur lors de l\'analyse');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectCapture = (id: string) => {
    setSelectedCapture(id);
    setGuardrailResult(null);
    const cap = captures.find((c) => c.id === id);
    if (cap) setElements([]);
  };

  const handleGuardrail = async () => {
    if (!selectedCapture) return;
    const required = guardInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (!required.length) { setError('Entrez au moins un élément requis'); return; }

    setRunningGuard(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_CONFIG.baseURL}${API_ENDPOINTS.rpaAdminGuardrail}`,
        {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ capture_id: selectedCapture, required_elements: required }),
        },
      );
      if (!resp.ok) throw new Error(await resp.text());
      const result: GuardrailResult = await resp.json();
      setGuardrailResult(result);
      await loadCaptures();
    } catch {
      setError('Erreur guardrail');
    } finally {
      setRunningGuard(false);
    }
  };

  return (
    <div>
      <h2 className="fw-bold mb-1">
        <i className="bi bi-eye me-2 text-primary"></i>
        Vision / RPA
      </h2>
      <p className="text-muted mb-4">
        Cartographie des écrans DPI. Upload un screenshot → analyse PIL → guardrail avant action.
      </p>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-circle"></i>{error}
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2">
          <i className="bi bi-check-circle"></i>{successMsg}
        </div>
      )}

      <div className="row g-4">
        {/* Upload + Analyse */}
        <div className="col-12 col-lg-5">
          <div className="card">
            <div className="card-header">
              <i className="bi bi-upload me-2 text-primary"></i>
              <strong>Analyser un écran</strong>
            </div>
            <div className="card-body">
              <form onSubmit={handleUpload}>
                <div className="mb-3">
                  <label className="form-label">Nom de l'écran</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ex: dpi_saisie_patient"
                    value={screenName}
                    onChange={(e) => setScreenName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Screenshot (PNG/JPG, max 2 MB)</label>
                  <input
                    ref={fileRef}
                    type="file"
                    className="form-control"
                    accept="image/png,image/jpeg"
                  />
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={uploading}>
                  {uploading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Analyse en cours…</>
                    : <><i className="bi bi-cpu me-2" />Analyser</>
                  }
                </button>
              </form>

              {/* Éléments détectés */}
              {elements.length > 0 && (
                <div className="mt-3">
                  <p className="text-muted small mb-2">
                    <strong>{elements.length}</strong> éléments détectés :
                  </p>
                  <div className="d-flex flex-wrap gap-1">
                    {elements.map((el, i) => (
                      <span
                        key={i}
                        className={`badge ${TYPE_BADGE[el.type] ?? 'bg-secondary'}`}
                        title={`bbox: [${el.bbox.join(', ')}] conf: ${el.confidence}`}
                      >
                        {el.label} ({el.type})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Guardrail */}
          {selectedCapture && (
            <div className="card mt-3">
              <div className="card-header">
                <i className="bi bi-shield-check me-2 text-warning"></i>
                <strong>Guardrail</strong>
              </div>
              <div className="card-body">
                <div className="mb-2">
                  <label className="form-label small">
                    Éléments requis (séparés par virgule)
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="ex: element_1, element_2"
                    value={guardInput}
                    onChange={(e) => setGuardInput(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-warning btn-sm w-100"
                  onClick={handleGuardrail}
                  disabled={runningGuard}
                >
                  {runningGuard
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><i className="bi bi-shield-check me-1" />Vérifier</>
                  }
                </button>

                {guardrailResult && (
                  <div className={`alert mt-2 mb-0 ${guardrailResult.ok ? 'alert-success' : 'alert-danger'}`}>
                    <strong>{guardrailResult.ok ? '✅ OK' : '❌ KO'}</strong>
                    {' '}Score : {Math.round(guardrailResult.score * 100)}%
                    <br />
                    <small>{guardrailResult.reason}</small>
                    {guardrailResult.missing.length > 0 && (
                      <div className="mt-1">
                        <small>Manquants : <code>{guardrailResult.missing.join(', ')}</code></small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Liste captures */}
        <div className="col-12 col-lg-7">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <span>
                <i className="bi bi-images me-2 text-primary"></i>
                <strong>Captures récentes</strong>
              </span>
              <button className="btn btn-outline-secondary btn-sm" onClick={loadCaptures}>
                <i className="bi bi-arrow-clockwise" />
              </button>
            </div>
            <div className="card-body p-0">
              {loadingCaptures && (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" />
                </div>
              )}
              {!loadingCaptures && captures.length === 0 && (
                <p className="text-muted text-center py-4 mb-0">
                  Aucune capture — uploadez un screenshot ci-contre.
                </p>
              )}
              {captures.map((cap, idx) => (
                <div
                  key={cap.id}
                  className={`px-3 py-2 d-flex align-items-center gap-3 cursor-pointer ${selectedCapture === cap.id ? 'bg-primary-subtle' : ''}`}
                  style={{
                    borderBottom: idx < captures.length - 1 ? '1px solid var(--gray-100)' : 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSelectCapture(cap.id)}
                >
                  <div className="flex-grow-1">
                    <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{cap.screen_name}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {cap.nb_elements} élément{cap.nb_elements > 1 ? 's' : ''}
                      {cap.captured_at && ` · ${new Date(cap.captured_at).toLocaleString('fr-FR')}`}
                    </div>
                  </div>
                  <span className={`badge ${GUARDRAIL_BADGE[cap.guardrail_status]}`}
                    style={{ fontSize: '0.7rem' }}>
                    {cap.guardrail_status}
                  </span>
                  {selectedCapture === cap.id && (
                    <i className="bi bi-chevron-right text-primary" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
